import React, { Component } from 'react';
import { connect } from 'react-redux';
import { withFormik } from 'formik';
import Yup from 'yup';
import history from 'pages/router/history';
import { Route, Switch } from 'react-router-dom';
import { RoutedTabs, NavTab } from 'react-router-tabs';
import get from 'lodash/get';
import Card, { CardSection } from '@lightspeed/cirrus-card';
import { Spinner } from '@lightspeed/flame/Spinner';

import { actionTypes } from 'pages/store/actions/defaults';
import { getData, putData, postData, deleteData } from 'pages/store/actions/data';
import { getValue, getProcessing, getErrors } from 'pages/store/selectors';
import { entities } from 'pages/store/schema';
import { showErrors, formatErrorsForFormik } from 'pages/utils/errors';
import BrandsActionBar from './BrandsActionBar';
import { paths as brandsPaths } from '../routes';

import Details from './Details';
import Merge from './Merge';

const setBreadcrumbs = ({ text, name }) => {
  window.merchantos.asyncSubscribe.notifySubscribers('react.mount.topbar', {
    props: {
      crumbs: [
        {
          type: 'ui_tab',
          text: 'Inventory',
          name: 'inventory',
        },
        {
          text: 'Brands',
          page: brandsPaths.listing,
        },
        {
          text,
          varText: name,
        },
      ],
    },
  });
};

class BrandsView extends Component {
  constructor(props) {
    super(props);

    this.handleClickDelete = this.handleClickDelete.bind(this);
  }

  componentDidMount() {
    const id = get(this, 'props.match.params.id');
    const { brand = {} } = this.props;
    const name = this.isNew() ? 'New' : get(brand, 'attributes.name');

    setBreadcrumbs({ name });

    if (!this.isNew()) {
      this.props.getData(`/admin/brands/${id}`);
    }
  }

  componentDidUpdate(prevProps) {
    const {
      brand = {},
      loadingErrors,
      savingErrors,
      deletingErrors,
      resetForm,
      setSubmitting,
      isSaving,
      isDeleting,
      setErrors,
    } = this.props;
    const name = this.isNew() ? 'New' : get(brand, 'attributes.name');

    setBreadcrumbs({ name });

    if (prevProps.isSaving && !isSaving) {
      resetForm();
      if (savingErrors) {
        setErrors(formatErrorsForFormik(get(savingErrors, 'response.data.errors')));
      }
      setSubmitting(false);
    }

    if (prevProps.isDeleting && !isDeleting) {
      history.replace(brandsPaths.listing);
      window.flashNotifier('success', 'Brand deleted.', 3);
    }

    if (!prevProps.loadingErrors && loadingErrors) {
      showErrors(loadingErrors);
      history.replace(brandsPaths.listing);
    }

    if (!prevProps.savingErrors && savingErrors) {
      showErrors(savingErrors);
    }

    if (!prevProps.deletingErrors && deletingErrors) {
      showErrors(deletingErrors);
    }
  }

  handleClickDelete() {
    const { brand = {} } = this.props;
    this.props.deleteData(`/admin/brands/${brand.id}`);
  }

  isNew() {
    return !get(this.props, 'match.params.id');
  }

  render() {
    const {
      brand,
      isSubmitting,
      isDeleting,
      values,
      setFieldValue,
      handleSubmit,
      submitForm,
      dirty,
      errors,
      location,
    } = this.props;

    let { isLoading } = this.props;

    isLoading = !brand && isLoading;

    const id = get(brand, 'id') || get(this.props, 'match.params.id');
    const componentProps = {
      isLoading,
      handleSubmit,
      location,
      id,
      values,
      errors,
      isSubmitting,
      setFieldValue,
    };

    const pathsWithID = {
      details: brandsPaths.details.replace(':id', id),
      merge: brandsPaths.merge.replace(':id', id),
    };

    return (
      <div className="cirrus">
        <BrandsActionBar
          submitForm={submitForm}
          dirty={dirty}
          isLoading={isLoading}
          isSubmitting={isSubmitting}
          isDeleting={isDeleting}
          onSubmit={handleSubmit}
          onDelete={this.isNew() ? null : this.handleClickDelete}
        />
        <div className="react-page__view">
          <Card>
            <CardSection noSpacing>
              <RoutedTabs className="react-page__tabs">
                <NavTab to={this.isNew() ? brandsPaths.new : pathsWithID.details}>Details</NavTab>
                {!this.isNew() && <NavTab to={pathsWithID.merge}>Merge</NavTab>}
              </RoutedTabs>
            </CardSection>
          </Card>
          <div className="react-page__section">
            {!this.isNew() && !brand && (
              <div className="react-page__spinner">
                <Spinner size="2rem" color="gray-300" />
              </div>
            )}
            <Switch>
              <Route
                path={`(${pathsWithID.details}|${brandsPaths.new})`}
                render={() => <Details {...componentProps} />}
              />
              {!this.isNew() && (
                <Route path={pathsWithID.merge} render={() => <Merge {...componentProps} />} />
              )}
            </Switch>
          </div>
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state, ownProps) => {
  const { id } = ownProps.match.params;
  return {
    brand: getValue(entities.brand, id)(state),
    isLoading: getProcessing({
      entity: entities.brand,
      list: false,
      actionType: actionTypes.LOADING,
    })(state),
    isSaving: getProcessing({
      entity: entities.brand,
      list: false,
      actionType: actionTypes.SAVING,
    })(state),
    isDeleting: getProcessing({
      entity: entities.brand,
      list: false,
      actionType: actionTypes.DELETING,
    })(state),
    loadingErrors: getErrors({
      entity: entities.brand,
      list: false,
      actionType: actionTypes.LOADING,
    })(state),
    savingErrors: getErrors({
      entity: entities.brand,
      list: false,
      actionType: actionTypes.SAVING,
    })(state),
    deletingErrors: getErrors({
      entity: entities.brand,
      list: false,
      actionType: actionTypes.DELETING,
    })(state),
  };
};

const mapDispatchToProps = dispatch => ({
  getData: url => dispatch(getData({ url, entity: entities.brand, list: false })),
  postDataBrands: (url, data, onSuccess) =>
    dispatch(postData({ url, payload: data, entity: entities.brand, list: false, onSuccess })),
  putDataBrands: (url, data, onSuccess) =>
    dispatch(putData({ url, payload: data, entity: entities.brand, list: false, onSuccess })),
  deleteData: (url, onSuccess) =>
    dispatch(deleteData({ url, entity: entities.brand, list: false, onSuccess })),
});

const formSettings = {
  enableReinitialize: true,
  mapPropsToValues: ({ brand }) => ({
    name: get(brand, 'attributes.name', ''),
  }),
  validationSchema: Yup.object().shape({
    name: Yup.string().required('Brand name is required'),
  }),
  handleSubmit: async (
    values,
    { props: { brand = {}, putDataBrands, postDataBrands }, setSubmitting }
  ) => {
    try {
      setSubmitting(true);
      const { name } = values;

      if (brand.id) {
        await putDataBrands(`/admin/brands/${brand.id}`, { brand: { name } });
        window.flashNotifier('success', 'Brand updated.', 3);
      } else {
        await postDataBrands('/admin/brands/', { brand: { name } }, response => {
          if (window.location.pathname === brandsPaths.new) {
            history.replace(brandsPaths.details.replace(':id', get(response, 'data.id')));
          }
          window.flashNotifier('success', 'Brand created.', 3);
        });
      }
    } catch (error) {
      // silence error from here, handling is done inside componentDidUpdate
    }
  },
};

const ConnectedBrandsView = connect(
  mapStateToProps,
  mapDispatchToProps
)(withFormik(formSettings)(BrandsView));

export default ConnectedBrandsView;
